/* eslint-disable @typescript-eslint/dot-notation */
import { inject, Injectable } from '@angular/core';
import { NbToastrService } from '@nebular/theme';

import { API } from '@aws-amplify/api';
import { Auth } from '@aws-amplify/auth';
import { Store } from '@ngxs/store';
import { Observable, from, catchError, of, tap } from 'rxjs';
import { CampaignState } from 'src/app/features/campaigns/redux';
import { ContentState } from 'src/app/features/content/redux';
import {
  BuildDrbFail,
  BuildDrbSuccess,
  LoadDiffFail,
  LoadDiffSuccess,
  LoadDrb,
  loadDrbAssociatedItemsFail,
  loadDrbAssociatedItemsSuccess,
  LoadDrbByVersionFail,
  LoadDrbByVersionSuccess,
  LoadDrbIdsAndValuesFail,
  LoadDrbIdsAndValuesSuccess,
  LoadDrbIdsFail,
  LoadDrbIdsSuccess,
  LoadDrbSuccess,
  LoadDrbVersionsSuccess,
  NoDrbDiff,
  NoDrbDraft,
  SaveComplete,
  SaveFail
} from 'src/app/features/drbs/redux/drb.actions';
import { Campaign } from 'src/app/models/campaign';
import { Content } from 'src/app/models/content';
import { Drb } from 'src/app/models/drb-models/drb';
import { APIS } from 'src/app/shared/constants/api-constants';
import { ENVIRONMENTS } from 'src/app/shared/constants/general-constants';
import { AmplifyServiceBuilder } from 'src/app/shared/utils/amplify.service.builder';
import { ENVIRONMENT } from 'src/environments/environment';

@Injectable({ providedIn: 'any' })
export class DrbService {
  public content$: Observable<any[]> = inject(Store).select(ContentState.content);
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);

  public drb_api = APIS.DRB;
  public campaigns: Campaign[];
  public contents: Content[];

  constructor(private store: Store, private toastrService: NbToastrService) {
    this.campaign$.subscribe((data) => (this.campaigns = data));
    this.content$.subscribe((data) => (this.contents = data));
  }

  public setupAffectedItems(item: any, env: string) {
    if ('payload' in item) {
      const payload = item.payload;
      if (env === ENVIRONMENTS.STAGE) {
        let stageCampaigns = [];
        let stageContent = [];
        if ('stage_campaign_ids' in payload || 'stage_content_ids' in payload) {
          stageCampaigns = this.campaigns.filter((campaign) =>
            payload.stage_campaign_ids.includes(campaign.id.toString())
          );
          stageContent = this.contents.filter((content) => payload.stage_content_ids.includes(content.content_id));

          return [stageCampaigns, stageContent];
        }
      } else if (env === ENVIRONMENTS.PROD) {
        if ('prod_campaign_ids' in payload || 'prod_content_ids' in payload) {
          return [payload.prod_campaign_ids, payload.prod_content_ids];
        }
      }
    } else {
      return [[], []];
    }
  }

  public mapResponse(response) {
    const localResponse = JSON.parse(JSON.stringify(response));
    return localResponse;
  }

  public async loadDrb(bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.loadDrbAmplify(token, bundle_name);
    });
  }

  public loadDrbAmplify(token, bundle_name, reload?) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { token, version: 'draft', bundle_name });
    API.get(this.drb_api, `/${ENVIRONMENT.name}/drb`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbSuccess(this.mapResponse(response)));
      })
      .catch((error) => {
        if (error.response.data.error === 'Draft version not found') {
          this.store.dispatch(NoDrbDraft);
        } else {
          this.toastrService.show('', $localize`Fetching DRB Failed`, {
            status: 'danger'
          });
        }
      });
  }

  public async loadDrbByVersion(version: string, bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.loadDrbByVersionAmplify(token, version, bundle_name);
    });
  }

  public loadDrbByVersionAmplify(token, version, bundle_name, reload?) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { token, version, bundle_name });
    API.get(this.drb_api, `/${ENVIRONMENT.name}/drb`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB ${version} Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbByVersionSuccess(this.mapResponse(response)));
      })
      .catch((error) => {
        this.toastrService.show('', $localize`Fetching DRB ${version} Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new LoadDrbByVersionFail());
      });
  }

  public loadDrbAssociatedItems(drb_id: string) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null);

    API.get(this.drb_api, `/${ENVIRONMENT.name}/associated-resources?drb_id=${drb_id}`, requestData)
      .then((response) => {
        this.toastrService.show('', $localize`Fetching DRB Associated Items Complete`, {
          status: 'success'
        });
        this.store.dispatch(new loadDrbAssociatedItemsSuccess(response));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Fetching DRB Associated Items Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new loadDrbAssociatedItemsFail());
      });
  }

  public putDrbAmplify(newDrbDraft: Drb) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, newDrbDraft);
    API.put(this.drb_api, `/${ENVIRONMENT.name}/drb`, requestData)
      .then((response) => {
        this.store.dispatch(new SaveComplete(this.mapResponse(newDrbDraft)));
      })
      .catch((error) => {
        this.store.dispatch(new SaveFail(error));
      });
  }

  public postDrbAmplify(newDrbDraft: Drb, bundle_name: string) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, newDrbDraft);
    API.post(this.drb_api, `/${ENVIRONMENT.name}/drb`, requestData)
      .then((response) => {
        this.store.dispatch(new LoadDrb(bundle_name));
      })
      .catch((error) => {
        this.store.dispatch(new SaveFail(error));
      });
  }

  public async loadDrbVersions(bundle_name: string) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.loadDrbVersionsAmplify(token, bundle_name);
    });
  }

  public loadDrbVersionsAmplify(token, bundle_name: string, reload?) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { token, all: true, bundle_name });

    API.get(this.drb_api, `/${ENVIRONMENT.name}/drb`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Versions Complete`, {
            status: 'success'
          });
        }
        this.store.dispatch(new LoadDrbVersionsSuccess(response, bundle_name));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Fetching DRB Versions Failed`, {
          status: 'danger'
        });
      });
  }

  public async loadDiff(bundle_name) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.loadDiffAmplify(token, bundle_name);
    });
  }

  public loadDiffAmplify(token, bundle_name, reload?) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { token, bundle_name });
    const endpoint = ENVIRONMENT.isProduction ? 'validate-prod' : 'validate';
    API.get(this.drb_api, `/${ENVIRONMENT.name}/${endpoint}`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Fetching DRB Diff Complete`, {
            status: 'success'
          });
        }
        const data = response;
        this.store.dispatch(new LoadDiffSuccess(data));
      })
      .catch((error) => {
        if (error.response.data === 'No DRBs available for validation') {
          this.store.dispatch(NoDrbDiff);
        } else {
          this.toastrService.show('', $localize`Fetching DRB Diff Failed`, {
            status: 'danger'
          });
          this.store.dispatch(new LoadDiffFail());
        }
      });
  }

  public async buildDrb(bundle_name) {
    // Fetch token for authorizer lambda pass the token in the API request
    await Auth.currentSession().then((res) => {
      const accessToken = res.getAccessToken();
      const token = accessToken.getJwtToken();
      this.buildDrbAmplify(bundle_name, token);
    });
  }

  public buildDrbAmplify(bundle_name, token, reload?) {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, { bundle_name }, { token });
    const endpoint = ENVIRONMENT.isProduction ? 'deploy-prod' : 'build';
    API.post(this.drb_api, `/${ENVIRONMENT.name}/${endpoint}`, requestData)
      .then((response) => {
        if (reload) {
          this.toastrService.show('', $localize`Building DRB Started`, {
            status: 'success'
          });
        }
        this.store.dispatch(new BuildDrbSuccess({ draft_version: 'SUCCEDDED' }));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Building DRB Failed`, {
          status: 'danger'
        });
        this.store.dispatch(BuildDrbFail);
      });
  }

  public loadDrbIds() {
    API.get(this.drb_api, `/${ENVIRONMENT.name}/drb-ids`, {})
      .then((response) => {
        this.toastrService.show('', $localize`Fetching DRB IDs Complete`, {
          status: 'success'
        });
        this.store.dispatch(new LoadDrbIdsSuccess(response));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Fetching DRB IDs Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new LoadDrbIdsFail(error));
      });
  }

  public loadDrbIdsAndValues() {
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { includeValues: true });
    API.get(this.drb_api, `/${ENVIRONMENT.name}/drb-ids`, requestData)
      .then((response) => {
        this.toastrService.show('', $localize`Fetching DRB IDs and values Complete`, {
          status: 'success'
        });
        this.store.dispatch(new LoadDrbIdsAndValuesSuccess(response));
      })
      .catch((error) => {
        console.error(error);
        this.toastrService.show('', $localize`Fetching DRB IDs and values Failed`, {
          status: 'danger'
        });
        this.store.dispatch(new LoadDrbIdsAndValuesFail(error));
      });
  }

  public loadDrbValues(drbIds: any[]) {
    this.toastrService.show(status, $localize`Fetching drb values`, {
      status: 'info'
    });
    const requestData = AmplifyServiceBuilder(this.drb_api, false, null, { drb_ids: drbIds });
    return API.get(this.drb_api, `/${ENVIRONMENT.name}/drb-values`, requestData);
  }
}
